import { useState, useEffect, useCallback, useRef } from 'react';
import userService from '../services/userService';

export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoaded = useRef(false);

  // Cargar usuarios
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await userService.getUsers();
      setUsers(data.users || data);
    } catch (err) {
      setError(err.message || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  // Crear usuario
  const createUser = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const newUser = await userService.createUser(userData);
      setUsers(prev => [...prev, newUser.user]);
      return newUser;
    } catch (err) {
      setError(err.message || 'Error al crear usuario');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Actualizar usuario
  const updateUser = useCallback(async (userId, userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedUser = await userService.updateUser(userId, userData);
      setUsers(prev => prev.map(user => 
        user.id === userId ? updatedUser : user
      ));
      return updatedUser;
    } catch (err) {
      setError(err.message || 'Error al actualizar usuario');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Eliminar usuario
  const deleteUser = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    
    try {
      await userService.deleteUser(userId);
      setUsers(prev => prev.filter(user => user.id !== userId));
    } catch (err) {
      setError(err.message || 'Error al eliminar usuario');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Cambiar estado de usuario
  const toggleUserStatus = useCallback(async (userId, status) => {
    setLoading(true);
    setError(null);

    try {
      const updatedUser = await userService.toggleUserStatus(userId, status);
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, status: status } : user
      ));
      return updatedUser;
    } catch (err) {
      setError(err.message || 'Error al cambiar estado del usuario');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Cambiar contraseña
  const changePassword = useCallback(async (userId, passwordData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await userService.changePassword(userId, passwordData);
      return result;
    } catch (err) {
      setError(err.message || 'Error al cambiar contraseña');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar usuarios al montar el componente - SOLUCIONADO
  useEffect(() => {
    // Evitar llamadas duplicadas en modo estricto
    if (hasLoaded.current) {
      return;
    }
    
    hasLoaded.current = true;
    loadUsers();
  }, [loadUsers]);

  return {
    users,
    loading,
    error,
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
    toggleUserStatus,
    changePassword,
  };
};
